// src/pages/Reports/cards/SalesReportCard.jsx
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
// import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';

import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { fetchSalesSummaryReport } from '../../../api';

// const NoBorderTextField = styled(TextField)({
//   width: '100%',
//   '& .MuiOutlinedInput-root': {
//     borderRadius: 10,
//     backgroundColor: '#f3f3f6',
//     '& fieldset': { border: 'none' },
//   },
//   '& .MuiOutlinedInput-input': {
//     color: '#333',
//     fontSize: '0.95rem',
//     fontWeight: 500,
//     '&::placeholder': { color: '#888', opacity: 1 },
//   },
// });

const TAB_KEYS = [
  { key: 'TODAY', label: 'Today' },
  { key: 'LAST_7', label: '7 Days' },
  { key: 'LAST_30', label: '30 Days' },
  { key: 'THIS_MONTH', label: 'This Month' },
];

const getDateString = (date) => date.toISOString().slice(0, 10);

const getDateRangeFromTab = (tabKey) => {
  const today = new Date();
  const to = getDateString(today);

  if (tabKey === 'TODAY') {
    return { from: to, to };
  }

  if (tabKey === 'LAST_7') {
    const fromDate = new Date();
    fromDate.setDate(today.getDate() - 6);
    return { from: getDateString(fromDate), to };
  }

  if (tabKey === 'LAST_30') {
    const fromDate = new Date();
    fromDate.setDate(today.getDate() - 29);
    return { from: getDateString(fromDate), to };
  }

  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: getDateString(monthStart), to };
};

const formatMoney = (value) => {
  return `Rs. ${Number(value || 0).toLocaleString('en-LK', {
    maximumFractionDigits: 2,
  })}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-LK');

// const getItemName = (item) => {
//   return (
//     item.medicineName ||
//     item.brandName ||
//     item.genericName ||
//     'Unknown item'
//   );
// };

const formatExcelDateOnly = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-LK');
};

const formatExcelTimeOnly = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString('en-LK', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const setColumnWidths = (worksheet, widths) => {
  worksheet['!cols'] = widths.map((width) => ({ wch: width }));
};

const buildSalesReportRows = (reportData) => {
  return (reportData?.itemSalesDetails || []).map((item) => ({
    'Invoice No': item.invoiceNo || '',
    Date: formatExcelDateOnly(item.dateTime),
    Time: formatExcelTimeOnly(item.dateTime),
    'Payment Method': item.paymentMethod || '',
    Item: item.itemName || '',
    Batch: item.batchNo || '',
    Qty: item.qty || 0,
    'Unit Price': item.unitPrice || 0,
    'Bill Discount': item.billDiscountShare || 0,
    'Line Total': item.finalLineTotal || 0,
    'Invoice Total': item.invoiceTotal || 0,
  }));
};

const downloadSalesExcel = ({ filename, reportData, from, to }) => {
  if (!reportData) return;

  const summary = reportData.summary || {};
  const paymentBreakdown = summary.paymentBreakdown || {};
  const salesRows = buildSalesReportRows(reportData);

  const sheetRows = [
    ['Sales Report Summary'],
    [''],
    ['Report From', from],
    ['Report To', to],
    ['Total Sales', summary.totalSales || 0],
    ['Invoice Count', summary.invoiceCount || 0],
    ['Items Sold', summary.itemsSold || 0],
    ['Average Bill Value', summary.averageBillValue || 0],
    ['Total Discounts', summary.totalDiscount || 0],
    ['Cash Sales', paymentBreakdown.cash || 0],
    ['Card Sales', paymentBreakdown.card || 0],
    ['Other Payments', paymentBreakdown.other || 0],
    [''],
    ['Sales Details'],
    [
      'Invoice No',
      'Date',
      'Time',
      'Payment Method',
      'Item',
      'Batch',
      'Qty',
      'Unit Price',
      'Bill Discount',
      'Line Total',
      'Invoice Total',
    ],
    ...salesRows.map((row) => [
      row['Invoice No'],
      row.Date,
      row.Time,
      row['Payment Method'],
      row.Item,
      row.Batch,
      row.Qty,
      row['Unit Price'],
      row['Bill Discount'],
      row['Line Total'],
      row['Invoice Total'],
    ]),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);

  setColumnWidths(worksheet, [
    22, // Invoice No / summary metric
    16, // Date / summary value
    14, // Time
    18, // Payment Method
    32, // Item
    16, // Batch
    10, // Qty
    14, // Unit Price
    16, // Bill Discount
    16, // Line Total
    16, // Invoice Total
  ]);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Report');

  XLSX.writeFile(workbook, filename);
};

const SalesReportCard = () => {
  const defaultRange = getDateRangeFromTab('THIS_MONTH');

  const [tab, setTab] = useState('THIS_MONTH');
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);

  const [reportData, setReportData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadReport = async (params = { from, to }) => {
    try {
      setLoading(true);
      setError('');

      const res = await fetchSalesSummaryReport(params);
      setReportData(res.data);
    } catch (err) {
      console.error('Failed to fetch sales report:', err);
      setError(err?.response?.data?.message || 'Failed to load sales report.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport({ from, to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (_e, value) => {
    setTab(value);

    const range = getDateRangeFromTab(value);
    setFrom(range.from);
    setTo(range.to);

    loadReport(range);
  };

  const handleFromChange = (e) => {
    const value = e.target.value;
    setFrom(value);
    setTab(false);
    loadReport({ from: value, to });
  };

  const handleToChange = (e) => {
    const value = e.target.value;
    setTo(value);
    setTab(false);
    loadReport({ from, to: value });
  };

  const summary = reportData?.summary || {};
  const paymentBreakdown = summary.paymentBreakdown || {};
  // const topSellingItems = reportData?.topSellingItems || [];

  // const csvRows = useMemo(() => {
  //   if (!reportData) return [];

  //   const summaryRows = [
  //     { section: 'Sales Summary', metric: 'From', value: from },
  //     { section: 'Sales Summary', metric: 'To', value: to },
  //     { section: 'Sales Summary', metric: 'Total Sales', value: summary.totalSales || 0 },
  //     { section: 'Sales Summary', metric: 'Invoice Count', value: summary.invoiceCount || 0 },
  //     { section: 'Sales Summary', metric: 'Items Sold', value: summary.itemsSold || 0 },
  //     { section: 'Sales Summary', metric: 'Average Bill Value', value: summary.averageBillValue || 0 },
  //     { section: 'Sales Summary', metric: 'Total Discount', value: summary.totalDiscount || 0 },
  //     { section: 'Payment Breakdown', metric: 'Cash Sales', value: paymentBreakdown.cash || 0 },
  //     { section: 'Payment Breakdown', metric: 'Card Sales', value: paymentBreakdown.card || 0 },
  //     { section: 'Payment Breakdown', metric: 'Other Sales', value: paymentBreakdown.other || 0 },
  //   ];

  //   const itemRows = topSellingItems.map((item) => ({
  //     section: 'Top Selling Items',
  //     metric: getItemName(item),
  //     value: item.revenue || 0,
  //     batchNo: item.batchNo || '',
  //     qtySold: item.qtySold || 0,
  //   }));

  //   return [...summaryRows, ...itemRows];
  // }, [reportData, summary, paymentBreakdown, topSellingItems, from, to]);

  const handleDownload = () => {
    downloadSalesExcel({
      filename: `sales-report-${from}-to-${to}.xlsx`,
      reportData,
      from,
      to,
    });
  };

  const previewRows = buildSalesReportRows(reportData);

  return (
    <>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '22px',
          p: { xs: 2, sm: 3 },
          border: '1px solid #e5e7eb',
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.07)',
          background: '#fff',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: '16px',
              background: '#ecfeff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ReceiptLongOutlinedIcon
              sx={{
                color: '#0891b2',
                fontSize: 30,
              }}
            />
          </Box>

          <Chip
            label={`${formatNumber(summary.invoiceCount)} ${
              summary.invoiceCount === 1 ? 'sale' : 'sales'
            }`}
            sx={{
              borderRadius: '999px',
              background: '#cffafe',
              color: '#155e75',
              fontWeight: 800,
              fontSize: 14,
              height: 34,
              px: 1,
            }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: 23,
            fontWeight: 900,
            color: '#111827',
            mb: 1,
          }}
        >
          Sales Report
        </Typography>

        <Typography
          sx={{
            fontSize: 15.5,
            lineHeight: 1.65,
            color: '#6b7280',
            mb: 2.5,
          }}
        >
          Revenue, invoices, items sold, discounts and payment totals for a selected date range.
        </Typography>

        {/* Date controls */}
        <Box
          sx={{
            mb: 2.5,
            borderRadius: '20px',
            backgroundColor: '#f3f4f6',
            p: 1.4,
          }}
        >
          {/* Quick range buttons */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(4, 1fr)',
              },
              gap: 0.8,
            }}
          >
            {TAB_KEYS.map((item) => {
              const active = tab === item.key;

              return (
                <Button
                  key={item.key}
                  onClick={() => handleTabChange(null, item.key)}
                  disableRipple
                  sx={{
                    height: 40,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 900,
                    fontSize: { xs: '0.82rem', sm: '0.9rem' },
                    letterSpacing: '0.01em',
                    backgroundColor: active ? '#ffffff' : 'transparent',
                    color: active ? '#111827' : '#6b7280',
                    boxShadow: active
                      ? 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), 0 4px 12px rgba(15, 23, 42, 0.10)'
                      : 'none',
                    '&:hover': {
                      backgroundColor: active ? '#ffffff' : 'rgba(255,255,255,0.45)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>

          {/* Divider */}
          <Box
            sx={{
              height: '1px',
              backgroundColor: '#e5e7eb',
              mt: 1.4,
              mb: 1.4,
            }}
          />

          {/* Date fields */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
              },
              gap: 1.4,
            }}
          >
            <DateInput label="From" value={from} onChange={handleFromChange} />

            <DateInput label="To" value={to} onChange={handleToChange} />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Main metrics */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(3, 1fr)',
            },
            gap: 1.2,
            mb: 2.5,
          }}
        >
          <MiniMetric label="Revenue" value={formatMoney(summary.totalSales)} />
          <MiniMetric label="Invoices" value={formatNumber(summary.invoiceCount)} />
          <MiniMetric label="Items Sold" value={formatNumber(summary.itemsSold)} />
        </Box>

        {/* Buttons */}
        <Box sx={{ mt: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '150px 1fr',
            },
            gap: 1.4,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setPreviewOpen(true)}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              borderColor: '#e5e7eb',
              color: '#374151',
              fontWeight: 800,
              fontSize: 15,
              textTransform: 'none',
              background: '#fff',
              '&:hover': {
                borderColor: '#cbd5e1',
                background: '#f9fafb',
              },
            }}
          >
            Preview
          </Button>

          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
            onClick={handleDownload}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              background: '#0f172a',
              color: '#fff',
              fontWeight: 900,
              fontSize: 15,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                background: '#111827',
                boxShadow: 'none',
              },
              '&:disabled': {
                background: '#94a3b8',
                color: '#fff',
              },
            }}
          >
            Download Excel
          </Button>
        </Box>
        </Box>
      </Card>

      <SalesReportPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        from={from}
        to={to}
        summary={summary}
        paymentBreakdown={paymentBreakdown}
        rows={previewRows}
      />
    </>
  );
};

const DateInput = ({ label, value, onChange }) => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: '#6b7280',
          mb: 0.7,
          ml: 0.3,
        }}
      >
        {label}
      </Typography>

      <TextField
        type="date"
        size="small"
        value={value}
        onChange={onChange}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            height: 52,
            borderRadius: '15px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            '& fieldset': {
              border: 'none',
            },
            '&:hover': {
              borderColor: '#d1d5db',
            },
            '&.Mui-focused': {
              borderColor: '#94a3b8',
              boxShadow: '0 0 0 3px rgba(148, 163, 184, 0.18)',
            },
          },
          '& .MuiOutlinedInput-input': {
            fontSize: '1rem',
            fontWeight: 800,
            color: '#333',
            px: 1.6,
          },
        }}
      />
    </Box>
  );
};

const MiniMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        borderRadius: '14px',
        background: '#f8fafc',
        border: '1px solid #eef2f7',
        p: 1.4,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: '#6b7280',
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: 15,
          color: '#111827',
          fontWeight: 900,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const SalesReportPreviewModal = ({
  open,
  onClose,
  from,
  to,
  summary,
  paymentBreakdown,
  rows,
}) => {
  const visibleRows = rows || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '22px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 900,
              color: '#111827',
            }}
          >
            Sales Report Preview
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: '#6b7280',
              mt: 0.4,
            }}
          >
            {from} to {to}
          </Typography>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          backgroundColor: '#f8fafc',
        }}
      >
        <Box sx={{ p: 2.5, backgroundColor: '#ffffff' }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 900,
              color: '#111827',
              mb: 1.5,
            }}
          >
            Summary
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 1.4,
            }}
          >
            <PreviewMetric label="Total Sales" value={formatMoney(summary.totalSales)} />
            <PreviewMetric label="Invoices" value={formatNumber(summary.invoiceCount)} />
            <PreviewMetric label="Items Sold" value={formatNumber(summary.itemsSold)} />
            <PreviewMetric label="Average Bill Value" value={formatMoney(summary.averageBillValue)} />
            <PreviewMetric label="Total Discounts" value={formatMoney(summary.totalDiscount)} />
            <PreviewMetric label="Cash Sales" value={formatMoney(paymentBreakdown.cash)} />
            <PreviewMetric label="Card Sales" value={formatMoney(paymentBreakdown.card)} />
            <PreviewMetric label="Other Payments" value={formatMoney(paymentBreakdown.other)} />
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 900,
              color: '#111827',
            }}
          >
            Sales Details
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: '#6b7280',
              fontWeight: 600,
              mt: 0.4,
            }}
          >
            These are the same columns included in the downloaded Excel sheet.
          </Typography>
        </Box>

        {visibleRows.length === 0 ? (
          <Box sx={{ p: 4 }}>
            <Typography color="text.secondary">No sales found for this date range.</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: '60vh',
              overflow: 'auto',
              backgroundColor: '#ffffff',
            }}
          >
            <Box
  sx={{
    p: 2,
    backgroundColor: '#ffffff',
  }}
>
  <Box
    sx={{
      maxHeight: '55vh',
      overflow: 'auto',
      border: '1px solid #e5e7eb',
      borderRadius: '14px',
      backgroundColor: '#ffffff',
    }}
  >
    <Table
      stickyHeader
      size="small"
      sx={{
        minWidth: 1250,
        '& th': {
          backgroundColor: '#f3f4f6',
          color: '#374151',
          fontWeight: 900,
          whiteSpace: 'nowrap',
          borderBottom: '1px solid #d1d5db',
        },
        '& td': {
          whiteSpace: 'nowrap',
          borderBottom: '1px solid #eef2f7',
          fontSize: 13,
        },
      }}
    >
              <TableHead>
                <TableRow>
                  <TableCell>Invoice No</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Bill Discount</TableCell>
                  <TableCell align="right">Line Total</TableCell>
                  <TableCell align="right">Invoice Total</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleRows.map((row, index) => (
                  <TableRow key={`${row['Invoice No']}-${index}`}>
                    <TableCell>{row['Invoice No']}</TableCell>
                    <TableCell>{row.Date}</TableCell>
                    <TableCell>{row.Time}</TableCell>
                    <TableCell>{row['Payment Method']}</TableCell>
                    <TableCell>{row.Item}</TableCell>
                    <TableCell>{row.Batch}</TableCell>
                    <TableCell align="right">{formatNumber(row.Qty)}</TableCell>
                    <TableCell align="right">{formatMoney(row['Unit Price'])}</TableCell>
                    <TableCell align="right">{formatMoney(row['Bill Discount'])}</TableCell>
                    <TableCell align="right">{formatMoney(row['Line Total'])}</TableCell>
                    <TableCell align="right">{formatMoney(row['Invoice Total'])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PreviewMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        backgroundColor: '#f8fafc',
        border: '1px solid #e5e7eb',
        borderRadius: '14px',
        p: 1.5,
      }}
    >
      <Typography
        sx={{
          color: '#6b7280',
          fontSize: 12,
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: '#111827',
          fontSize: 17,
          fontWeight: 900,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export default SalesReportCard;